import {
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from 'react'

import { Combobox } from '@headlessui/react'
import { clsx } from 'clsx'
import TextareaAutosize from 'react-textarea-autosize'
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import useMobileDetect from 'use-mobile-detect-hook'

import { IconButton } from '@tribeplatform/react-ui-kit/Button'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'

import { useCommandBar } from '../../CommandBar/CommandBarContext.js'
import { useI18n } from '../../i18n/index.js'
import { ResultsView, SmartSearchTabs } from '../index.js'
import {
  MAX_AI_PROMPT_LENGTH,
  SMART_SEARCH_HEADER_INPUT_DEFAULT_HEIGHT,
} from '../SmartSearch.constants.js'
import {
  SmartSearchFilters,
  SmartSearchTabsType,
} from '../SmartSearch.types.js'

export type SmartSearchHeaderRef = {
  focus: () => void
  blur: () => void
  getHeight: () => number
}

type Props = {
  filters: SmartSearchFilters
  setFilters: React.Dispatch<React.SetStateAction<SmartSearchFilters>>
  activeTab: SmartSearchTabsType
  className?: string
  onBack: () => void
  view: ResultsView
  placeholder: string
  aiEnabled: boolean
}

export const SmartSearchHeader = forwardRef<SmartSearchHeaderRef, Props>(
  (
    {
      filters,
      setFilters,
      activeTab,
      className,
      onBack,
      view,
      placeholder,
      aiEnabled,
    },
    ref,
  ) => {
    const { type, askAIQuery } = filters
    const { close, searchOptions } = useCommandBar()
    const [isFocused, setIsFocused] = useState(false)
    const [inputHeight, setInputHeight] = useState(0)
    const { $t } = useI18n()
    const inputRef = useRef<HTMLInputElement>(null)
    const { isMobile } = useMobileDetect()

    const hasAskAiQuery = askAIQuery?.length > 0
    const placeholderText = useMemo(() => {
      if (placeholder) {
        return placeholder
      }

      if (activeTab === SmartSearchTabs.SEARCH) {
        if (aiEnabled) {
          return $t({
            id: 'SmartSearch.Search.Placeholder',
            defaultMessage: 'Search or ask a question...',
          })
        }

        return $t({
          id: 'Generic.SearchDotDotDot',
          defaultMessage: 'Search...',
        })
      }

      return hasAskAiQuery
        ? $t({
            id: 'SmartSearch.AskAI.Placeholder.WithoutQuery',
            defaultMessage: 'Ask AI anything...',
          })
        : $t({
            id: 'SmartSearch.AskAI.Placeholder.WithQuery',
            defaultMessage: 'Ask anything or select...',
          })
    }, [$t, activeTab, aiEnabled, hasAskAiQuery, placeholder])
    // console.log(placeholderText)
    const sideItemsClassName = 'flex items-center h-9'
    const bgClassName =
      view === 'command-bar'
        ? 'bg-surface-disabled lg:bg-surface'
        : 'bg-surface'

    const isOverflowing =
      inputHeight > SMART_SEARCH_HEADER_INPUT_DEFAULT_HEIGHT && isFocused

    const handleClose = () => {
      close()
    }

    const setQuery = (query: string) => {
      setFilters(prev => ({
        ...prev,
        query,
      }))
    }

    const isRenderingResultsForSolelyType = !!type && view === 'command-bar'

    const handleOnFocus = () => {
      setIsFocused(true)
      // put the cursor at the end of the query
      inputRef.current?.setSelectionRange(
        filters.query.length,
        filters.query.length,
      )
    }

    const handleClearQuery = () => {
      setQuery('')

      if (isMobile()) {
        setFilters(prev => ({
          ...prev,
          type: searchOptions.entityType ?? null,
        }))
      }
    }

    useImperativeHandle(ref, () => ({
      focus: () => inputRef.current?.focus(),
      blur: () => inputRef.current?.blur(),
      getHeight: () => inputRef.current?.clientHeight,
    }))

    return (
      <header
        className={clsx(
          'flex flex-col sticky top-0 bg-surface min-h-16',
          className,
          view === 'command-bar' && 'z-50',
        )}
      >
        <div className={clsx('relative')}>
          <div
            className={clsx('flex flex-1 w-full bg-surface z-50', {
              'shadow-2xl': isOverflowing,
              absolute: isOverflowing,
            })}
          >
            <div className="flex py-3 flex-1 overflow-hidden px-4 lg:px-5 items-center gap-2 lg:gap-0">
              <div className={clsx(sideItemsClassName, 'self-start')}>
                {(activeTab === SmartSearchTabs.ASK_AI ||
                  isRenderingResultsForSolelyType) && (
                  <IconButton
                    rounded
                    variant="secondaryNeutral"
                    size="md"
                    icon={<SvgIcon name="arrow-left" size="lg" />}
                    onClick={onBack}
                    className="lg:hidden"
                  />
                )}

                {!isRenderingResultsForSolelyType &&
                  view === 'command-bar' &&
                  activeTab === SmartSearchTabs.SEARCH && (
                    <IconButton
                      rounded
                      variant="secondaryNeutral"
                      size="md"
                      onClick={handleClose}
                      icon={<SvgIcon name="close" size="lg" />}
                      className="lg:hidden"
                    />
                  )}
              </div>

              <div
                className={clsx(
                  'flex gap-1 lg:gap-2 rounded-md px-2 lg:px-0 flex-1 overflow-hidden',
                  bgClassName,
                )}
              >
                <div className={sideItemsClassName}>
                  {activeTab === SmartSearchTabs.ASK_AI ? (
                    <IconButton
                      rounded
                      variant="secondaryNeutral"
                      size="md"
                      icon={<SvgIcon name="arrow-left" size="lg" />}
                      onClick={onBack}
                      tabIndex={-1}
                      className="hidden lg:block"
                    />
                  ) : (
                    <SvgIcon
                      name="search"
                      size="lg"
                      className="text-content-disabled"
                    />
                  )}
                </div>

                <Combobox.Input
                  as={TextareaAutosize}
                  displayValue={() => filters.query}
                  className={clsx(
                    'border-none outline-none leading-7 lg:text-heading-2xs resize-none flex flex-1 py-1',
                    bgClassName,
                    !isFocused && 'overflow-hidden',
                  )}
                  placeholder={placeholderText}
                  value={filters.query}
                  onChange={event => setQuery(event.target.value)}
                  autoFocus
                  ref={inputRef}
                  onHeightChange={setInputHeight}
                  onBlur={() => setIsFocused(false)}
                  onFocus={handleOnFocus}
                  maxLength={MAX_AI_PROMPT_LENGTH}
                  maxRows={isFocused ? undefined : 1}
                  style={{ height: SMART_SEARCH_HEADER_INPUT_DEFAULT_HEIGHT }} // default height to avoid layout shift, later it'd be adjusted based on the input height
                />

                <div className={sideItemsClassName}>
                  <IconButton
                    rounded
                    variant="plainNeutral"
                    tabIndex={-1}
                    size="xs"
                    icon={
                      <SvgIcon
                        name="close"
                        size="sm"
                        className="text-surface"
                      />
                    }
                    onClick={handleClearQuery}
                    className={clsx(
                      '!bg-background-divider',
                      filters.query ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>
    )
  },
)
